import { Alert, Autocomplete, Grid, InputAdornment, MenuItem, Paper, TextField, Tooltip, Typography } from "@mui/material";
import { useLookupTables } from "../../../../../hooks/useLookupTables";
import { Billing, BillingsWithCashFlow, CashFlowOptions } from "../CashFlow";
import { useMemo } from "react";
import PreLoader from "../../../../PreLoader";
import { Info } from "@mui/icons-material";
import { useAxios } from "../../../../../hooks/useAxios";
import { NewViewCustomer } from "../../types";
import { ViewDefaultBilling } from "../../DefaultBillings";
import { DateTime } from "luxon";
import { allowMoneyFormat, formatNumberToMoney } from "../../../../Utils";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";

type Props = {
  customerIds?: number[]
  error: boolean
  form: BillingsWithCashFlow
  netValue: number
  updateBilling: (updates: Partial<Billing>) => void
  updateCashFlowOptions: (updates: Partial<CashFlowOptions>) => void
}

export default function BillingComponent({ customerIds, error, form, netValue, updateBilling, updateCashFlowOptions }: Props) {
  // Hooks
  const { lookupTables, errorLookupTables, isLoadingLookupTables } = useLookupTables(['affiliations', 'currencies', 'customerTypes', 'services'])
  // Memos
  const isEdit = useMemo(() => Boolean(form.billing.id), [form.billing])

  // Customers
  const { data: customers, error: errorCustomers, isLoading: isLoadingCustomers } = useAxios<NewViewCustomer[]>(`${process.env.REACT_APP_MAIN_BACKEND_URL}/customers`)

  console.log('customers', customers)

  const beneficiaryOptions = useMemo(() => customers?.filter((_) => _.fk_id_customer_type === form.billing.beneficiaryType)?.map((_) => ({ id: _.id, label: _.customer_name, dependence_id: _.dependence_id })) || [], [customers, form.billing])
  const payerOptions = useMemo(() => customers?.filter((_) => _.fk_id_customer_type === form.billing.payerType).map((_) => ({ id: _.id, label: _.customer_name, dependence_id: _.dependence_id })) || [], [customers, form.billing])
  const payeeOptions = useMemo(() => customers?.filter((_) => _.fk_id_customer_type === 10).map((_) => ({ id: _.id, label: _.customer_name })) || [], [customers, form.billing])
  const presetCustomers = useMemo(() => customers?.filter((_) => customerIds?.includes(_.id)) || [], [customers, customerIds])

  // Default Billings
  const { data: defaultBillings, error: errorDefaultBillings, isLoading: isLoadingDefaultBillings } = useAxios<ViewDefaultBilling[]>(`${process.env.REACT_APP_MAIN_BACKEND_URL}/default-billings`)

  const defaultBillingsOptions = useMemo(() => defaultBillings?.filter((_) => _.fk_id_affiliation === form.billing.affiliation?.id).map((_) => ({ id: _.id, label: _.default_billing_name })) || [], [defaultBillings, form.billing])

  if (isLoadingLookupTables || isLoadingCustomers || isLoadingDefaultBillings) return <PreLoader height={'400px'} />

  if (errorLookupTables || errorCustomers || errorDefaultBillings || !lookupTables) return <Alert severity="error">Could not load lookup tables.</Alert>

  return <>
    <Grid item xs={12}>
      <Typography>
        Billing
      </Typography>
    </Grid>
    <Grid item xs={6}>
      <Autocomplete
        id='form-field-affiliation'
        options={lookupTables?.affiliations || []}
        value={form.billing.affiliation}
        onChange={(e, newValue) => updateBilling({
          affiliation: newValue,
          // Deselect beneficiary if affiliation is cleared
          beneficiary: !isEdit && !newValue ? null : form.billing.beneficiary
        })}
        renderInput={params => <TextField {...params} label='Affiliation' />}
        disabled={isEdit}
      />
    </Grid>
    <Grid item xs={6}>
      <Autocomplete
        id='form-field-default-billing'
        options={defaultBillingsOptions}
        value={form.billing.defaultBilling}
        onChange={(e, newValue) => {
          const _newBilling: Partial<Billing> = {
            defaultBilling: newValue
          }

          // Filling other information of this billing
          const _defaultBilling = defaultBillings?.find((_) => _.id === newValue?.id)
          const _payer = _defaultBilling?.fk_id_customer_type === 1 ? (payerOptions.find((_) => _.dependence_id === _defaultBilling?.fk_id_affiliation) || null) : null
          const _service = _defaultBilling ? (lookupTables.services.find((_) => _.id === _defaultBilling?.fk_id_service) || null) : null

          // console.log(newValue, defaultBillings, _defaultBilling, _payer, _service)

          if (_defaultBilling) {
            _newBilling.totalBilling = {
              label: _defaultBilling.default_value.toFixed(2),
              value: _defaultBilling.default_value
            }
            _newBilling.service = _service
            _newBilling.currency = {
              id: _defaultBilling.fk_id_currency,
              sign: lookupTables.currencies.find((_) => _.id === _defaultBilling.fk_id_currency)?.sign || ''
            }
            _newBilling.payerType = _defaultBilling.fk_id_customer_type
            _newBilling.payer = _payer
            _newBilling.payee = _defaultBilling.fk_id_payee

            updateCashFlowOptions({ numberOfBills: { label: _defaultBilling?.max_installments.toString(), value: _defaultBilling?.max_installments } })
          }

          updateBilling(_newBilling)
        }}
        renderInput={params => <TextField {...params} label='Default Billing' />}
        getOptionKey={(option) => option.id}
        disabled={isEdit}
      />
    </Grid>
    <Grid item xs={5}>
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <DatePicker
          slotProps={{ textField: { id: 'form-field-issue-date' } }}
          format="dd-MMM-yy"
          label='Issue Date'
          value={form.billing.issueDate}
          onChange={(newValue) => updateBilling({ issueDate: newValue })}
          disabled={isEdit}

        />
      </LocalizationProvider>
    </Grid>
    <Grid item xs={7}>
      <Autocomplete
        id='form-field-service'
        options={lookupTables?.services || []}
        value={form.billing.service}
        onChange={(e, newValue) => updateBilling({ service: newValue })}
        renderInput={params => <TextField {...params} label='Service' />}
        disabled={isEdit}
      />
    </Grid>
    <Grid item xs={5}>
      <TextField
        id='form-field-beneficiary-type'
        label='Beneficiary type'
        value={form.billing.beneficiaryType || ''}
        onChange={(e) => {
          updateBilling({
            beneficiaryType: Number(e.target.value),
            // If type affiliation, select the customer of the affiliation
            beneficiary: Number(e.target.value) === 1 ? (beneficiaryOptions.find((_) => _.dependence_id === form.billing.affiliation?.id) || null) : null
          })
        }}
        disabled={isEdit}
        select
      >
        {
          lookupTables.customerTypes
            .filter((type) => [1, 2].includes(type.id)) // Affiliation and Student
            .map((type, index) => <MenuItem key={index} id={`beneficiary-type-option-${type.id}`} value={type.id}>{type.label}</MenuItem>)
        }
      </TextField>
    </Grid>
    <Grid item xs={7}>
      {
        customerIds?.length && form.billing.beneficiaryType === 2 ?
          <Paper sx={{ padding: '0 10px', height: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography>
              From selected list.
            </Typography>
            <Tooltip title={presetCustomers.map((_, index) => <p key={index}>{_.customer_name}</p>)}>
              <Info />
            </Tooltip>
          </Paper>
          :
          <Autocomplete
            id='form-field-beneficiary'
            options={beneficiaryOptions}
            value={form.billing.beneficiary}
            onChange={(e, newValue) => updateBilling({ beneficiary: newValue })}
            renderInput={params => <TextField {...params} label='Beneficiary' />}
            getOptionLabel={(option) => option.label || 'Unknown'}
            disabled={!form.billing.affiliation || isEdit}
          />
      }
    </Grid>
    <Grid item xs={5}>
      <TextField
        id='form-field-payer-type'
        label='Payer type'
        value={form.billing.payerType || ''}
        onChange={(e) => {
          updateBilling({
            payerType: Number(e.target.value),
            // If type affiliation, select the customer of the affiliation
            payer: Number(e.target.value) === 1 ? (payerOptions.find((_) => _.dependence_id === form.billing.affiliation?.id) || null) : null
          })
        }}
        disabled={isEdit}
        select
      >
        {
          lookupTables.customerTypes
            .filter((type) => [1, 2].includes(type.id)) // Affiliation and Student
            .map((type, index) => <MenuItem key={index} id={`payer-type-option-${type.id}`} value={type.id}>{type.label}</MenuItem>)
        }
      </TextField>
    </Grid>
    <Grid item xs={7}>
      {
        customerIds?.length && form.billing.payerType === 2 ?
          <Paper sx={{ padding: '0 10px', height: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography>
              From selected list.
            </Typography>
            <Tooltip title={presetCustomers.map((_, index) => <p key={index}>{_.customer_name}</p>)}>
              <Info />
            </Tooltip>
          </Paper>
          :
          <Autocomplete
            id='form-field-payer'
            options={payerOptions}
            value={form.billing.payer}
            onChange={(e, newValue) => updateBilling({ payer: newValue })}
            renderInput={params => <TextField {...params} label='Payer' />}
            disabled={!form.billing.affiliation || isEdit}
          />
      }
    </Grid>
    <Grid item xs={6}>
      <TextField
        id='form-field-payee'
        label='Payee'
        value={form.billing.payee || ''}
        onChange={(e) => updateBilling({ payee: Number(e.target.value) })}
        disabled={isEdit}
        select
      >
        {
          payeeOptions.map((_, index) => <MenuItem key={index} id={`payee-option-${_.id}`} value={_.id}>{_.label}</MenuItem>)
        }
      </TextField>
    </Grid>
    <Grid item xs={6}>
      <TextField
        id='form-field-reference-year'
        label='Reference year'
        value={form.billing.referenceYear || ''}
        onChange={(e) => updateBilling({ referenceYear: Number(e.target.value) })}
        disabled={isEdit}
        select
      >
        {
          [-1, 0, 1].map((_, index) => <MenuItem key={index} value={DateTime.utc().get('year') + _}>{DateTime.utc().get('year') + _}</MenuItem>)
        }
      </TextField>
    </Grid>
    <Grid item xs={3}>
      <TextField
        id='form-field-currency'
        label='Currency'
        value={form.billing.currency.id || ''}
        onChange={(e) => updateBilling({
          currency: {
            id: Number(e.target.value),
            sign: lookupTables.currencies.find((_) => _.id === Number(e.target.value))?.sign || ''
          }
        })}
        disabled={isEdit}
        select
      >
        {
          lookupTables.currencies.map((_, index) => <MenuItem key={index} id={`currency-option-${_.id}`} value={_.id}>{_.label}</MenuItem>)
        }
      </TextField>
    </Grid >
    <Grid item xs={9}>
      <TextField
        id='form-field-total-billing'
        label='Total Billing'
        value={form.billing.totalBilling.label}
        onChange={(e) => updateBilling({
          totalBilling: {
            value: allowMoneyFormat(e.target.value.trim()),
            label: e.target.value.trim()
          }
        })}
        error={error}
        onBlur={() => updateBilling({
          totalBilling: {
            ...form.billing.totalBilling,
            label: formatNumberToMoney(form.billing.totalBilling.value)
          }
        })}
        onFocus={() => updateBilling({
          totalBilling: {
            ...form.billing.totalBilling,
            label: form.billing.totalBilling.value.toFixed(2)
          }
        })}
        InputProps={{
          startAdornment: <InputAdornment position='start'>{form.billing.currency.sign}</InputAdornment>,
          sx: netValue !== form.billing.totalBilling.value && !isNaN(netValue) ? { textDecoration: 'line-through', color: '#888' } : {}
        }}
        disabled={Boolean(form.billing.defaultBilling) || isEdit}
      />
    </Grid>
  </>
}
